import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSite } from '../context/SiteContext';
import '../assets/LegalPage.css';

const PrivacyPolicy = () => {
  const { siteName, fullAddress, email, phone } = useSite();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const effectiveDate = 'March 19, 2025';
  const lastUpdated = 'March 19, 2025';

  return (
    <div className="legal-page">
      <div className="legal-container">
        <header className="legal-header">
          <h1>Privacy Policy</h1>
          <p className="legal-meta">
            Effective Date: {effectiveDate} | Last Updated: {lastUpdated}
          </p>
        </header>

        <div className="legal-content">
          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              {siteName} ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, make purchases, or interact with our services. This policy applies to all visitors and customers in the United States and is designed to comply with applicable federal and state privacy laws, including the California Consumer Privacy Act (CCPA), the California Privacy Rights Act (CPRA), the Virginia Consumer Data Protection Act (VCDPA), the Colorado Privacy Act (CPA), the Connecticut Data Privacy Act (CTDPA), and other applicable state privacy laws.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Categories of Personal Information We Collect</h2>
            <p>In the past 12 months, we have collected the following categories of personal information:</p>
            <ul>
              <li><strong>Identifiers:</strong> Name, email address, postal address, phone number, IP address, device identifiers, account credentials</li>
              <li><strong>Commercial Information:</strong> Purchase history, products viewed, wishlist items, cart contents</li>
              <li><strong>Internet or Network Activity:</strong> Browsing history, search history, interactions with our website</li>
              <li><strong>Geolocation Data:</strong> Approximate location derived from IP address</li>
              <li><strong>Payment Information:</strong> Credit card or debit card numbers (processed by payment processors; we do not store full card numbers)</li>
              <li><strong>Inferences:</strong> Preferences and characteristics drawn from the above data</li>
            </ul>
            <h3>Sensitive Personal Information</h3>
            <p>We may collect certain sensitive personal information when necessary for our services, such as:</p>
            <ul>
              <li>Account login credentials (username and password)</li>
              <li>Precise geolocation (only with your consent, for store locator features)</li>
            </ul>
            <p>We do not sell sensitive personal information. You have the right to limit our use of sensitive personal information as described in Section 9.</p>
          </section>

          <section className="legal-section">
            <h2>3. Sources of Personal Information</h2>
            <p>We collect personal information from the following sources:</p>
            <ul>
              <li><strong>Directly from you:</strong> When you create an account, make a purchase, sign up for emails, contact customer service, or interact with our website</li>
              <li><strong>Automatically:</strong> Through cookies, pixels, and similar technologies when you browse our website</li>
              <li><strong>From third parties:</strong> Payment processors, shipping carriers, analytics providers, and advertising partners</li>
              <li><strong>From publicly available sources:</strong> When we verify your identity or prevent fraud</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. Purposes for Collection and Use</h2>
            <p>We use your personal information for the following purposes:</p>
            <ul>
              <li>Process and fulfill orders, including shipping and returns</li>
              <li>Create and manage your account</li>
              <li>Send order confirmations, shipping updates, and customer service communications</li>
              <li>Send marketing communications (with your consent)</li>
              <li>Personalize your experience and provide product recommendations</li>
              <li>Improve our website, products, and services</li>
              <li>Detect and prevent fraud and unauthorized transactions</li>
              <li>Comply with legal obligations and respond to lawful requests</li>
              <li>Analyze website usage and trends</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies, web beacons, pixels, and similar technologies to collect information about your browsing and device. These technologies help us remember your preferences, understand how you use our site, and deliver relevant advertising. You can manage your cookie preferences through your browser settings or our cookie preference center.
            </p>
            <p>
              <strong>Types of cookies we use:</strong> Essential (necessary for site function), Analytics (to understand usage), Functional (to remember preferences), and Advertising (to deliver relevant ads). You may opt out of non-essential cookies through your browser or our settings.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Categories of Third Parties Receiving Data</h2>
            <p>We may disclose your personal information to the following categories of third parties:</p>
            <ul>
              <li><strong>Service providers:</strong> Payment processors, shipping carriers, email service providers, hosting providers</li>
              <li><strong>Analytics providers:</strong> To understand website usage and improve our services</li>
              <li><strong>Advertising partners:</strong> For personalized advertising (see "Sale or Sharing" below)</li>
              <li><strong>Professional advisors:</strong> Legal, accounting, or auditing services</li>
              <li><strong>Law enforcement:</strong> When required by law or to protect our rights</li>
            </ul>
            <p>We require service providers to use your information only for the purposes we specify and to protect it in accordance with this policy.</p>
          </section>

          <section className="legal-section" id="do-not-sell">
            <h2>7. Sale or Sharing of Personal Information</h2>
            <p>
              <strong>We do not sell personal information in the traditional sense.</strong> However, under the CCPA/CPRA, certain sharing of data for cross-context behavioral advertising may be considered "sharing." We may share identifiers, commercial information, and internet activity with advertising partners to deliver personalized ads. This may be considered a "sale" or "sharing" under California law.
            </p>
            <p>
              <strong>Your right to opt out:</strong> You have the right to opt out of the sale or sharing of your personal information. To exercise this right, please use one of the following methods:
            </p>
            <ul>
              <li>Click the "Do Not Sell or Share My Personal Information" link in our website footer</li>
              <li>Email us at <a href={`mailto:${email}`}>{email}</a></li>
              <li>Call us at <a href={`tel:${phone}`}>{phone}</a></li>
            </ul>
            <p>
              We honor Global Privacy Control (GPC) signals. If your browser or device sends a GPC signal, we will treat it as a valid opt-out request.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Data Retention</h2>
            <p>We retain your personal information for as long as necessary to fulfill the purposes described in this policy, unless a longer retention period is required by law. Specific retention periods include:</p>
            <ul>
              <li><strong>Account information:</strong> Until account deletion or 3 years of inactivity</li>
              <li><strong>Order and transaction records:</strong> 7 years for legal and tax compliance</li>
              <li><strong>Marketing preferences:</strong> Until you opt out or 3 years of inactivity</li>
              <li><strong>Website analytics:</strong> Up to 26 months</li>
              <li><strong>Customer service logs:</strong> 3 years</li>
            </ul>
            <p>After retention periods expire, we securely delete or anonymize your information.</p>
          </section>

          <section className="legal-section">
            <h2>9. Your Privacy Rights</h2>
            <h3>California Residents (CCPA/CPRA)</h3>
            <p>If you are a California resident, you have the right to:</p>
            <ul>
              <li><strong>Know:</strong> Request disclosure of the categories and specific pieces of personal information we have collected about you</li>
              <li><strong>Delete:</strong> Request deletion of your personal information, subject to certain exceptions</li>
              <li><strong>Correct:</strong> Request correction of inaccurate personal information</li>
              <li><strong>Opt out of sale/sharing:</strong> Opt out of the sale or sharing of your personal information</li>
              <li><strong>Limit sensitive data:</strong> Limit our use of sensitive personal information to what is necessary for business purposes</li>
              <li><strong>Non-discrimination:</strong> Not receive discriminatory treatment for exercising your privacy rights</li>
            </ul>
            <p>To exercise these rights, contact us at <a href={`mailto:${email}`}>{email}</a> or <a href={`tel:${phone}`}>{phone}</a>. We will verify your identity before processing your request. You may designate an authorized agent to make requests on your behalf.</p>

            <h3>Virginia, Colorado, Connecticut, and Other State Residents</h3>
            <p>Residents of Virginia, Colorado, Connecticut, Utah, Texas, and other states with comprehensive privacy laws may have similar rights, including the right to access, correct, delete, port, and opt out of sale/targeted advertising. Contact us using the methods above to exercise these rights.</p>
          </section>

          <section className="legal-section">
            <h2>10. Non-Discrimination</h2>
            <p>
              We will not discriminate against you for exercising your privacy rights. We will not deny you goods or services, charge different prices, provide a different level of service, or suggest that you will receive different treatment for exercising your rights under the CCPA or other applicable laws.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Children's Privacy</h2>
            <p>
              Our website is not intended for children under 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us at <a href={`mailto:${email}`}>{email}</a>, and we will delete such information. For children 13–16, we do not sell or share their personal information without affirmative authorization.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Data Security</h2>
            <p>
              We implement reasonable administrative, technical, and physical safeguards to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption of sensitive data in transit and at rest, access controls, and regular security assessments. However, no method of transmission over the Internet or electronic storage is 100% secure.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Privacy Policy Updates</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on this page and updating the "Last Updated" date. For significant changes, we may provide additional notice, such as an email or prominent notice on our website. We encourage you to review this policy periodically.
            </p>
          </section>

          <section className="legal-section">
            <h2>14. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:
            </p>
            <ul className="legal-contact">
              <li><strong>{siteName}</strong></li>
              <li>{fullAddress}</li>
              <li>Email: <a href={`mailto:${email}`}>{email}</a></li>
              <li>Phone: <a href={`tel:${phone}`}>{phone}</a></li>
            </ul>
            <p>
              For privacy-related requests, please include "Privacy Request" in your subject line for faster processing.
            </p>
          </section>
        </div>

        <div className="legal-footer-links">
          <Link to="/">Return to Home</Link>
          <Link to="/terms">Terms of Use</Link>
          <Link to="/shipping">Shipping Policy</Link>
          <Link to="/returns">Returns Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
